import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Backdrop
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { getLogisticsObjects, apiCall } from '../utils/api';
import { useNavigate } from 'react-router-dom';

const parseDateValue = (value) => {
  if (!value) {
    return { time: -1, isValid: false };
  }
  const date = new Date(value);
  const time = date.getTime();
  if (Number.isNaN(time)) {
    return { time: -1, isValid: false };
  }
  return { time, isValid: true };
};

const getNodeId = (node) => {
  if (!node) return '';
  if (typeof node === 'string') return node;
  return node['@id'] || '';
};

const getIdSegment = (node) => {
  const raw = getNodeId(node);
  if (!raw) return '';
  if (raw.startsWith('_:') || raw.startsWith(':')) return raw;
  if (raw.includes('#')) return raw.split('#').pop();
  if (raw.includes('/')) return raw.split('/').pop();
  return raw;
};

const getLiteralValue = (node) => {
  if (!node) return '';
  if (typeof node === 'string') return node;
  return node['@value'] || '';
};

const cleanupItem = (item) => {
  return {
    id: getIdSegment(item['@id']),
    status: getIdSegment(item['https://onerecord.iata.org/ns/api#hasRequestStatus']),
    requestor: getNodeId(item['https://onerecord.iata.org/ns/api#isRequestedBy']),
    requestTime: getLiteralValue(item['https://onerecord.iata.org/ns/api#isRequestedAt']),
    change: getIdSegment(item['https://onerecord.iata.org/ns/api#hasChange'])
  };
};

const STATUS_CHIP_CONFIG = {
  REQUEST_ACCEPTED: { label: 'Accepted', color: 'success', icon: <CheckIcon /> },
  REQUEST_REJECTED: { label: 'Rejected', color: 'error', icon: <CloseIcon /> },
  REQUEST_REVOKED: { label: 'Revoked', color: 'default', icon: <BlockIcon /> },
  REQUEST_FAILED: { label: 'Failed', color: 'error' }
};

const ActionButtons = ({ change, onStatusUpdate }) => {
  const navigate = useNavigate();
  const isPending = change.status === 'REQUEST_PENDING';
  const statusChip = STATUS_CHIP_CONFIG[change.status];
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
      <Tooltip title="View Details">
        <IconButton
          color="primary"
          onClick={() => navigate(`/changes-request/${change.id}`)}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
      
      {isPending ? (
        <>
          <Tooltip title="Approve">
            <IconButton
              color="success"
              onClick={() => onStatusUpdate(change.id, 'REQUEST_ACCEPTED')}
            >
              <CheckIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Reject">
            <IconButton
              color="error"
              onClick={() => onStatusUpdate(change.id, 'REQUEST_REJECTED')}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </>
      ) : statusChip ? (
        <Chip
          icon={statusChip.icon}
          label={statusChip.label}
          color={statusChip.color}
          size="small"
        />
      ) : (
        <Chip
          label={change.status || 'Unknown'}
          size="small"
        />
      )}
    </Box>
  );
};

const Changes = () => {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const formatDateTime = (value) => {
    const { time, isValid } = parseDateValue(value);
    if (!isValid) {
      return '-';
    }
    return new Date(time).toLocaleString();
  };

  const fetchChanges = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getLogisticsObjects('https%3A%2F%2Fonerecord.iata.org%2Fns%2Fapi%23ChangeRequest');
      
      const rawData = response['@graph'] ? response['@graph'] : [response];
      const cleanArray = rawData.filter(value => Object.keys(value).length !== 0);
      const cleanedData = cleanArray.map(cleanupItem);

      // Sort changes by request time in descending order (newest first)
      const sortedChanges = cleanedData.sort((a, b) => {
        return parseDateValue(b.requestTime).time - parseDateValue(a.requestTime).time;
      });

      setChanges(sortedChanges);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChanges();
  }, [fetchChanges]);

  const handleStatusUpdate = async (changeId, newStatus) => {
    try {
      setActionLoading(true);
      
      await apiCall(`/action-requests/${changeId}?status=${newStatus}`, {
        method: 'PATCH'
      });

      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchChanges();
      
    } catch (err) {
      setError(`Failed to update status: ${err.message}`);
    } finally {
      setTimeout(() => {
        setActionLoading(false);
      }, 300);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={actionLoading}
      >
        <CircularProgress color="inherit" />
        <Typography>Processing request...</Typography>
      </Backdrop>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EditIcon 
              sx={{ 
                fontSize: 40,
                color: '#1976d2'
              }} 
            />
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Changes
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchChanges}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        <Divider />
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={fetchChanges}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : changes.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No change requests found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>ID</TableCell>
                <TableCell>Requestor</TableCell>
                <TableCell>Change</TableCell>
                <TableCell>Request Time</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.map((change) => (
                <TableRow
                  key={change.id}
                  sx={{ 
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    backgroundColor: change.status !== 'pending' 
                      ? 'rgba(0, 0, 0, 0.02)' 
                      : 'inherit'
                  }}
                >
                  <TableCell>{change.id}</TableCell>
                  <TableCell>{change.requestor}</TableCell>
                  <TableCell>{change.change}</TableCell>
                  <TableCell>
                    {formatDateTime(change.requestTime)}
                  </TableCell>
                  <TableCell>
                    <ActionButtons 
                      change={change}
                      onStatusUpdate={handleStatusUpdate}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Changes;
